import { ORDER_TYPE_MODAL_ID } from '../modals/OrderTypeModal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import SubSection from './SubSection';
import { OrderingSelectors } from 'polygon-ordering';
import {
  CATERING_SALE_TYPES,
  DELIVERY_SALE_TYPES,
  SALE_TYPE,
} from '../libs/polygon-ordering/src/constants/saleType';
import getThemeLookup from '../selectors/getThemeLookup';
import { TEXT_PROPERTIES } from '../utils/theme';
import { DELIVERY_DETAILS_MODAL_ID } from '../modals/DeliveryDetailsModal';
import { LOCATION_MODAL_ID } from '../modals/LocationModal';
import SectionContainer from './SectionContainer';
import SubSectionChevron from './SubSectionChevron';

const {
  getSaleType,
  getLocationId,
  getTable,
  getBypassDeliveryDetails,
  getDeliveryAddressString,
  getEnabledSaleTypes,
} = OrderingSelectors;

const OrderTypeSummary: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const saleType = useAppSelector(getSaleType);
  const deliveryAddress = useAppSelector(getDeliveryAddressString);
  const table = useAppSelector(getTable);
  const locationId = useAppSelector(getLocationId);

  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);
  const isPickupOrTakeAway =
    saleType === SALE_TYPE.CATERING ||
    saleType === SALE_TYPE.PICKUP ||
    saleType === SALE_TYPE.TAKE_AWAY ||
    saleType === SALE_TYPE.WEB_ORDERING;
  const isDineIn = saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER;
  const missingTableNumber = isDineIn && !table;
  const displayTableText = t('tableOrder.orderingToTable', {
    tableNumber: missingTableNumber ? '' : table,
  });

  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);
  const desiredDeliveryAddressNotFound = useAppSelector(
    state => state.desiredDeliveryAddressNotFound,
  );

  const bypassedDetailsMissingAddress =
    bypassDeliveryDetails && (desiredDeliveryAddressNotFound || !deliveryAddress);

  const enabledSaleTypes = useAppSelector(getEnabledSaleTypes);
  const hasCateringDeliverySaletype = enabledSaleTypes.includes(SALE_TYPE.CATERING_DELIVERY);
  const isCatering = CATERING_SALE_TYPES.includes(saleType);

  const orderTypeString = `${t(`saleType.${[saleType]}.name`)} ${
    isCatering && hasCateringDeliverySaletype ? '- ' + t(`saleTypeChildren.${[saleType]}.name`) : ''
  }`;

  const getTitle = () => {
    if (isPickupOrTakeAway || isDelivery) {
      return orderTypeString;
    }

    if (isDineIn) {
      return displayTableText;
    }

    return '';
  };

  const getSubtitle = () => {
    if (isDelivery) {
      if (bypassedDetailsMissingAddress) {
        return 'Enter address';
      } else {
        return deliveryAddress;
      }
    } else {
      if (missingTableNumber) {
        return `* ${t('tableOrder.pleaseSelectATable')}`;
      } else {
        return '';
      }
    }
  };

  return (
    <SectionContainer>
      <SubSection
        title={getTitle()}
        subtitle={getSubtitle()}
        subtitleStyle={
          bypassedDetailsMissingAddress || missingTableNumber
            ? p('menuLocationHeaderTitleAction', TEXT_PROPERTIES)
            : { lineHeight: '185%', ...p('orderTypeDeliverySubtitleAddress', TEXT_PROPERTIES) }
        }
        action={() => {
          if (missingTableNumber) {
            dispatch(
              setCurrentModal({
                modalId: LOCATION_MODAL_ID,
                params: {
                  saleType,
                  locationId,
                  allowRedirect: false,
                  action: () => {
                    dispatch(setCurrentModal(null));
                  },
                },
              }),
            );
          } else if (bypassDeliveryDetails) {
            dispatch(
              setCurrentModal({
                modalId: DELIVERY_DETAILS_MODAL_ID,
                params: { saleType, allowRedirect: false },
              }),
            );
          } else {
            dispatch(
              setCurrentModal({
                modalId: ORDER_TYPE_MODAL_ID,
                params: { allowRedirect: false },
              }),
            );
          }
        }}
        rightSlot={() => <SubSectionChevron />}
      />
    </SectionContainer>
  );
};

export default OrderTypeSummary;
